/**
 * Mythium Rim Color — flat black acryl band, contour-stroke render
 *
 * Produces a PNG with a transparent buffer ring around the silhouette
 * mask, then a single flat black stroke around the OUTER contour of
 * the figure-plus-buffer shape — the Paperforge-style look.
 *
 * Why contour-stroke and not EDT-band-fill: filling pixels at
 * Euclidean distance (BUFFER, BUFFER+RIM] gives constant L2 thickness
 * but also paints the band on the INSIDE of any enclosed pocket
 * between touching features (bijl ↔ been), producing a closed inner
 * loop the merchant doesn't want. Tracing the outer contour of the
 * stroke-centerline shape and stroking it with lineWidth = RIM_BAND_PX
 * yields both:
 *   - Constant perpendicular thickness (canvas strokes are L2 by
 *     definition), and
 *   - No inner loops (only the outer contour is stroked; pockets
 *     stay fully transparent).
 *
 * Inputs: the cropped figure PNG (only used for its dimensions) and the
 * cutline mask PNG (the filled silhouette). Output: a PNG with the rim
 * stroke painted black, transparent everywhere else — laid behind the
 * figure by the renderer so the rim sits right around the outer
 * silhouette.
 */

(function () {
  'use strict';

  const ALPHA_THRESHOLD = 1;
  // Clear-acryl buffer between the figure and the black rim. This ring
  // stays transparent in the rim PNG so the preview-card grey shows
  // through, simulating bare acrylic material between the print and
  // the inked edge band.
  const ACRYL_BUFFER_PX = 24;
  // Thickness of the flat black rim band.
  const RIM_BAND_PX = 8;
  // Rim band colour. Pure black reads cleanly on every figure.
  const RIM_COLOR = [0, 0, 0];
  // Extra safety margin around the band so the stroke + Chaikin
  // smoothing + lineJoin rounding have room to extend beyond the
  // figure's silhouette without being clipped at the canvas edges
  // (typical: feet at the bottom row). Sized so the rim PNG has a full
  // 120 px of working room on every side of the source image.
  const SAFETY_MARGIN_PX = 120 - (ACRYL_BUFFER_PX + RIM_BAND_PX);
  // Total outward extent from the mask boundary.
  const TOTAL_BAND_PX = ACRYL_BUFFER_PX + RIM_BAND_PX;
  // Gaussian blur radius applied to the canvas-stroked rim PNG, then
  // re-thresholded at alpha > 128. Canvas stroke with lineJoin=round
  // already gives smooth corners; this blur+threshold extra-rounds
  // acute joints (where the centerline path turns sharply) without
  // changing the line's net thickness — the threshold sits at the
  // midpoint of the blur falloff. 0 = uit.
  const SMOOTH_BLUR_PX = 4;
  // Chaikin's corner-cutting iterations on each traced contour before
  // stroking. Each iteration replaces every vertex with two points at
  // 25 % / 75 % of its adjacent edges, which proportionally rounds
  // sharp direction changes without affecting flauwere overgangen.
  // 0 = uit, 1 = subtiel, 2 = duidelijk afgerond, 3+ = bijna-bezier.
  const CHAIKIN_ITERATIONS = 3;
  // Morphological closing radius (L2 px) applied to the stroke-centerline
  // shape before its contour is traced. Fills near-miss gaps between
  // figure parts (elf arm ↔ jurk-flap, bijl ↔ been) so the contour-trace
  // sees one merged silhouette and strokes a single soepele rand around
  // both. Gaps narrower than 2 × MERGE_GAP_PX get bridged; solitaire
  // features keep their outer contour unchanged.
  const MERGE_GAP_PX = 14;
  // Plinth down-fill. A flat rectangle of DOWNFILL_HEIGHT_PX height
  // attached directly below the figure's lowest opaque row. Width
  // is hybrid: always at least PLINTH_MIN_HALF_WIDTH on either side of
  // the figure's bbox centerX, but extended outward to the figure's
  // leftmost / rightmost opaque pixel in an attach-band just above
  // the bottom if those sit further out. So a narrow figure gets a
  // guaranteed minimum plinth; a wide figure with separated feet
  // gets a plinth that reaches both outer foot edges (mid-gap filled).
  // 0 (either constant) disables the down-fill.
  const DOWNFILL_HEIGHT_PX = 1;
  const PLINTH_MIN_HALF_WIDTH = 30;
  // Tab attached directly below the plinth, centered on the figure's
  // bbox center. Same width and height regardless of figure, so the
  // bottom of every mini reads as a consistent rounded tab below the
  // wider plinth body. 0 (either constant) disables the tab.
  const TAB_WIDTH_PX = 0;
  const TAB_HEIGHT_PX = 0;
  // 45° outward-and-up scan from each plinth top corner to detect a
  // separated leg sitting up-and-outside the plinth corner. If found,
  // a right-angle triangle is filled between the corner, the found
  // pixel, and the point directly above the corner at the found
  // pixel's height — bridging the diagonal gap so the rim wraps
  // plinth + leg as one contour. 0 disables the scan.
  const LEG_DETECT_RANGE_PX = 250;

  function loadImage(url) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = (e) => reject(e);
      img.src = url;
    });
  }

  function canvasToBlobUrl(canvas) {
    return new Promise((resolve, reject) => {
      canvas.toBlob((b) => {
        if (!b) return reject(new Error('toBlob failed'));
        resolve(URL.createObjectURL(b));
      }, 'image/png');
    });
  }

  const yieldToMain = () => new Promise(r => setTimeout(r, 0));

  /**
   * Exact Euclidean distance transform (Felzenszwalb-Huttenlocher).
   * Returns a Float64Array of length W*H containing squared distance to
   * the nearest mask=1 pixel. Inside the mask the value is 0.
   *
   * EDT is used instead of iterative 4-conn dilation because dilation
   * gives L1 (Manhattan) distance — the resulting band is 12 px wide
   * at axis-aligned contour segments but ~8.5 px at 45° (12/√2). EDT
   * yields constant perpendicular thickness in every direction, which
   * is what reads as a machine-cut rim instead of a hand-drawn line.
   */
  function edt1d(f, n, out, v, z) {
    let k = 0;
    v[0] = 0;
    z[0] = -Infinity;
    z[1] = Infinity;
    for (let q = 1; q < n; q++) {
      let s;
      while (true) {
        const vk = v[k];
        s = ((f[q] + q * q) - (f[vk] + vk * vk)) / (2 * (q - vk));
        if (s <= z[k] && k > 0) k--;
        else break;
      }
      k++;
      v[k] = q;
      z[k] = s;
      z[k + 1] = Infinity;
    }
    k = 0;
    for (let q = 0; q < n; q++) {
      while (z[k + 1] < q) k++;
      const vk = v[k];
      out[q] = (q - vk) * (q - vk) + f[vk];
    }
  }

  async function edt2d(mask, W, H) {
    const N = W * H;
    const INF = 1e20;
    const sq = new Float64Array(N);
    for (let i = 0; i < N; i++) sq[i] = mask[i] ? 0 : INF;

    const maxDim = Math.max(W, H);
    const v = new Int32Array(maxDim);
    const z = new Float64Array(maxDim + 1);
    const colF = new Float64Array(H);
    const colOut = new Float64Array(H);
    const rowF = new Float64Array(W);
    const rowOut = new Float64Array(W);

    // Pass 1: distance transform along columns.
    for (let x = 0; x < W; x++) {
      for (let y = 0; y < H; y++) colF[y] = sq[y * W + x];
      edt1d(colF, H, colOut, v, z);
      for (let y = 0; y < H; y++) sq[y * W + x] = colOut[y];
      if ((x & 0xFF) === 0xFF) await yieldToMain();
    }
    // Pass 2: distance transform along rows.
    for (let y = 0; y < H; y++) {
      const off = y * W;
      for (let x = 0; x < W; x++) rowF[x] = sq[off + x];
      edt1d(rowF, W, rowOut, v, z);
      for (let x = 0; x < W; x++) sq[off + x] = rowOut[x];
      if ((y & 0xFF) === 0xFF) await yieldToMain();
    }
    return sq;
  }

  /**
   * Moore-neighbor boundary trace, 8-connected, Jacob's stopping criterion.
   * Returns the pixel-center coordinates of one closed outer contour of
   * the connected component reached from (sx, sy). The seed is assumed
   * to be the topmost-leftmost pixel of its component (so it's
   * guaranteed to sit on the OUTER boundary, not an inner-hole boundary).
   */
  function traceContour(inside, W, H, sx, sy) {
    // Clockwise 8-neighbor offsets starting at East.
    const dx = [ 1, 1, 0, -1, -1, -1,  0,  1];
    const dy = [ 0, 1, 1,  1,  0, -1, -1, -1];
    const pts = [];
    let px = sx, py = sy;
    // Raster scan finds the start by going left-to-right, top-to-bottom,
    // so the previous step would have come from the west. Searching
    // begins one step clockwise from there (i.e. NW → N → NE → …).
    let backDir = 4;
    let firstDir = -1;
    pts.push(px, py);
    const maxSteps = 16 * (W + H);
    for (let step = 0; step < maxSteps; step++) {
      const startDir = (backDir + 1) & 7;
      let foundDir = -1;
      for (let i = 0; i < 8; i++) {
        const d = (startDir + i) & 7;
        const nx = px + dx[d], ny = py + dy[d];
        if (nx < 0 || ny < 0 || nx >= W || ny >= H) continue;
        if (inside[ny * W + nx]) { foundDir = d; break; }
      }
      if (foundDir < 0) break; // isolated pixel
      // Jacob's stop: back at start AND about to repeat the first move.
      if (pts.length > 2 && px === sx && py === sy && foundDir === firstDir) break;
      if (firstDir === -1) firstDir = foundDir;
      backDir = (foundDir + 4) & 7;
      px += dx[foundDir];
      py += dy[foundDir];
      pts.push(px, py);
    }
    return pts;
  }

  /**
   * Chaikin's corner-cutting on a closed polyline. Input is a flat
   * Float64-style array [x0, y0, x1, y1, …]; output is a new flat array
   * with ~2× the points. Each segment AB becomes two new points at
   * (0.75 A + 0.25 B) and (0.25 A + 0.75 B), proportionally rounding
   * sharp vertices.
   */
  function chaikinSmooth(pts, iterations) {
    let cur = pts;
    for (let iter = 0; iter < iterations; iter++) {
      const n = cur.length >> 1;
      if (n < 3) break;
      const next = new Float32Array(n * 4);
      for (let i = 0; i < n; i++) {
        const ax = cur[i * 2];
        const ay = cur[i * 2 + 1];
        const j = (i + 1) % n;
        const bx = cur[j * 2];
        const by = cur[j * 2 + 1];
        next[i * 4]     = 0.75 * ax + 0.25 * bx;
        next[i * 4 + 1] = 0.75 * ay + 0.25 * by;
        next[i * 4 + 2] = 0.25 * ax + 0.75 * bx;
        next[i * 4 + 3] = 0.25 * ay + 0.75 * by;
      }
      cur = next;
    }
    return cur;
  }

  /**
   * Build the rim PNG. Returns { url, padFraction } where padFraction
   * is the padding-amount-as-fraction-of-source-width that the renderer
   * uses to expand the rim's SVG bbox so the band below the figure is
   * fully drawn (the figure typically touches the cropped image's bottom
   * row, so the dilation needs canvas room beyond the source dimensions).
   * Returns null on failure.
   */
  async function buildRimColors(figureUrl, maskUrl) {
    if (!figureUrl || !maskUrl) return null;

    let figImg, maskImg;
    try {
      [figImg, maskImg] = await Promise.all([loadImage(figureUrl), loadImage(maskUrl)]);
    } catch (e) { return null; }
    const srcW = figImg.naturalWidth, srcH = figImg.naturalHeight;
    if (!srcW || !srcH) return null;
    if (maskImg.naturalWidth !== srcW || maskImg.naturalHeight !== srcH) return null;

    // Pad the working canvas so the band can extend symmetrically below
    // (and above/left/right) the figure even when the figure touches
    // the cropped source's edges.
    const PAD = TOTAL_BAND_PX + SAFETY_MARGIN_PX;
    const W = srcW + 2 * PAD;
    const H = srcH + 2 * PAD;

    const maskCanvas = document.createElement('canvas');
    maskCanvas.width = W; maskCanvas.height = H;
    const maskCtx = maskCanvas.getContext('2d', { willReadFrequently: true });
    if (!maskCtx) return null;
    maskCtx.drawImage(maskImg, PAD, PAD);
    let maskData;
    try { maskData = maskCtx.getImageData(0, 0, W, H).data; } catch (e) { return null; }

    const N = W * H;
    const maskOpaque = new Uint8Array(N);
    for (let i = 0; i < N; i++) {
      if (maskData[i * 4 + 3] >= ALPHA_THRESHOLD) maskOpaque[i] = 1;
    }

    // Plinth down-fill. A flat rectangle spanning the figure's full
    // bbox width (minX..maxX) and extending DOWNFILL_HEIGHT_PX downward
    // from the figure's lowest opaque row. Added to maskOpaque before
    // the EDT so it gets the same band + smoothing treatment as the
    // rest of the silhouette — Chaikin then rounds the bottom-corner
    // 90° turns.
    if (DOWNFILL_HEIGHT_PX > 0 && PLINTH_MIN_HALF_WIDTH > 0) {
      // Figure bbox: minX, maxX for centerX; maxY for attach height.
      let minX = W, maxX = -1, maxY = -1;
      for (let y = 0; y < H; y++) {
        const off = y * W;
        for (let x = 0; x < W; x++) {
          if (!maskOpaque[off + x]) continue;
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y > maxY) maxY = y;
        }
      }
      if (maxX >= 0 && maxY >= 0) {
        // Attach-band leftmost/rightmost opaque on rows just above maxY.
        const ATTACH_TOLERANCE_PX = 5;
        const bandTop = Math.max(0, maxY - ATTACH_TOLERANCE_PX);
        let figureLeftEdge = W, figureRightEdge = -1;
        for (let y = bandTop; y <= maxY; y++) {
          const off = y * W;
          for (let x = 0; x < W; x++) {
            if (!maskOpaque[off + x]) continue;
            if (x < figureLeftEdge) figureLeftEdge = x;
            if (x > figureRightEdge) figureRightEdge = x;
          }
        }
        if (figureRightEdge < figureLeftEdge) {
          figureLeftEdge = minX;
          figureRightEdge = maxX;
        }

        const centerX = (minX + maxX) >> 1;
        const plinthLeft  = Math.max(0,     Math.min(centerX - PLINTH_MIN_HALF_WIDTH, figureLeftEdge));
        const plinthRight = Math.min(W - 1, Math.max(centerX + PLINTH_MIN_HALF_WIDTH, figureRightEdge));
        const fillTop = maxY + 1;
        const fillBottom = Math.min(H - 1, maxY + DOWNFILL_HEIGHT_PX);
        for (let y = fillTop; y <= fillBottom; y++) {
          const off = y * W;
          for (let x = plinthLeft; x <= plinthRight; x++) maskOpaque[off + x] = 1;
        }

        // 45° leg-bridge scan from each plinth top corner. If an opaque
        // pixel is found up-and-outward within LEG_DETECT_RANGE_PX,
        // fill a right-angle triangle (corner, found pixel, point
        // straight above corner at found pixel's height) into the mask.
        if (LEG_DETECT_RANGE_PX > 0) {
          let leftLegX = -1, leftLegY = -1;
          for (let step = 1; step <= LEG_DETECT_RANGE_PX; step++) {
            const sx = plinthLeft - step;
            const sy = fillTop - step;
            if (sx < 0 || sy < 0) break;
            if (maskOpaque[sy * W + sx]) { leftLegX = sx; leftLegY = sy; break; }
          }
          let rightLegX = -1, rightLegY = -1;
          for (let step = 1; step <= LEG_DETECT_RANGE_PX; step++) {
            const sx = plinthRight + step;
            const sy = fillTop - step;
            if (sx >= W || sy < 0) break;
            if (maskOpaque[sy * W + sx]) { rightLegX = sx; rightLegY = sy; break; }
          }
          if (leftLegX >= 0 || rightLegX >= 0) {
            const triCanvas = document.createElement('canvas');
            triCanvas.width = W; triCanvas.height = H;
            const triCtx = triCanvas.getContext('2d', { willReadFrequently: true });
            if (triCtx) {
              triCtx.fillStyle = '#000';
              if (leftLegX >= 0) {
                triCtx.beginPath();
                triCtx.moveTo(plinthLeft, fillTop);
                triCtx.lineTo(leftLegX, leftLegY);
                triCtx.lineTo(plinthLeft, leftLegY);
                triCtx.closePath();
                triCtx.fill();
              }
              if (rightLegX >= 0) {
                triCtx.beginPath();
                triCtx.moveTo(plinthRight, fillTop);
                triCtx.lineTo(rightLegX, rightLegY);
                triCtx.lineTo(plinthRight, rightLegY);
                triCtx.closePath();
                triCtx.fill();
              }
              let triData;
              try { triData = triCtx.getImageData(0, 0, W, H).data; } catch (e) { triData = null; }
              if (triData) {
                for (let i = 0; i < N; i++) {
                  if (triData[i * 4 + 3] > 128) maskOpaque[i] = 1;
                }
              }
            }
          }
        }

        // Tab directly below the plinth, fixed width centered on centerX.
        if (TAB_WIDTH_PX > 0 && TAB_HEIGHT_PX > 0) {
          const tabHalf = TAB_WIDTH_PX >> 1;
          const tabLeft  = Math.max(0,     centerX - tabHalf);
          const tabRight = Math.min(W - 1, centerX + tabHalf);
          const tabTop = fillBottom + 1;
          const tabBottom = Math.min(H - 1, tabTop + TAB_HEIGHT_PX - 1);
          if (tabTop < H) {
            for (let y = tabTop; y <= tabBottom; y++) {
              const off = y * W;
              for (let x = tabLeft; x <= tabRight; x++) maskOpaque[off + x] = 1;
            }
          }
        }
      }
    }

    // EDT: squared L2 distance from each pixel to the nearest mask
    // pixel. The stroke is rendered along the contour of the shape
    // {p : d(p, mask) ≤ BUFFER + RIM/2} — the centerline of the band —
    // and canvas's lineWidth then puts BUFFER..BUFFER+RIM perpendicular
    // distance on either side. Constant L2 thickness by construction.
    const sqDist = await edt2d(maskOpaque, W, H);

    // Stroke-centerline shape: every pixel within (BUFFER + RIM/2) of mask.
    const centerDist = ACRYL_BUFFER_PX + RIM_BAND_PX / 2;
    const centerSq = centerDist * centerDist;
    const inside = new Uint8Array(N);
    for (let i = 0; i < N; i++) inside[i] = sqDist[i] <= centerSq ? 1 : 0;

    // Morphological closing of `inside` by MERGE_GAP_PX. Fills thin
    // gaps between near-touching features so the contour-trace sees a
    // single merged silhouette. Implemented as a second EDT pass: the
    // boundary of the dilated shape sits at (centerDist + MERGE_GAP_PX)
    // from the mask; eroding by MERGE_GAP_PX = "keep only pixels at
    // distance > MERGE_GAP_PX from the outside-of-dilated set".
    //
    // Solitaire features see no change: original `inside` pixels are
    // already MERGE_GAP_PX away from outsideDilated, so they all survive.
    // Outer dilation rings around solitaire features are within
    // MERGE_GAP_PX of outsideDilated → eroded away. Only the gap-fill
    // pixels between near-touching features get added.
    if (MERGE_GAP_PX > 0) {
      const dilatedRadius = centerDist + MERGE_GAP_PX;
      const dilatedRadiusSq = dilatedRadius * dilatedRadius;
      const outsideDilated = new Uint8Array(N);
      for (let i = 0; i < N; i++) {
        outsideDilated[i] = sqDist[i] > dilatedRadiusSq ? 1 : 0;
      }
      const sqDistFromOutside = await edt2d(outsideDilated, W, H);
      const mergeSq = MERGE_GAP_PX * MERGE_GAP_PX;
      for (let i = 0; i < N; i++) {
        if (sqDistFromOutside[i] > mergeSq) inside[i] = 1;
      }
    }

    // Trace the OUTER contour of every connected component. Raster
    // scan finds each component at its topmost-leftmost pixel, which is
    // guaranteed to sit on the outer boundary (not an inner-hole
    // boundary). A 4-conn flood from that pixel marks every pixel of
    // the component visited so we don't retrace, and so the inner
    // boundary of any enclosed pocket (a different topological loop of
    // the SAME component) is never traced — that's exactly the "no
    // inner loops at touching features" behaviour we want.
    const visited = new Uint8Array(N);
    const contours = [];
    const floodQueue = new Int32Array(N);
    for (let y = 0; y < H; y++) {
      if ((y & 0x3F) === 0) await yieldToMain();
      for (let x = 0; x < W; x++) {
        const idx = y * W + x;
        if (!inside[idx] || visited[idx]) continue;
        contours.push(traceContour(inside, W, H, x, y));
        // 4-conn flood-fill to mark the component visited.
        let fh = 0, ft = 0;
        floodQueue[ft++] = idx;
        visited[idx] = 1;
        while (fh < ft) {
          const cur = floodQueue[fh++];
          const cx = cur % W;
          const cy = (cur - cx) / W;
          if (cx > 0)     { const n = cur - 1; if (inside[n] && !visited[n]) { visited[n] = 1; floodQueue[ft++] = n; } }
          if (cx < W - 1) { const n = cur + 1; if (inside[n] && !visited[n]) { visited[n] = 1; floodQueue[ft++] = n; } }
          if (cy > 0)     { const n = cur - W; if (inside[n] && !visited[n]) { visited[n] = 1; floodQueue[ft++] = n; } }
          if (cy < H - 1) { const n = cur + W; if (inside[n] && !visited[n]) { visited[n] = 1; floodQueue[ft++] = n; } }
        }
      }
    }
    if (contours.length === 0) return null;

    // Stroke each contour on canvas. lineWidth = RIM_BAND_PX puts the
    // stroke RIM_BAND_PX/2 on either side of the centerline; round
    // join+cap keeps corners smooth without thickening the line.
    const outCanvas = document.createElement('canvas');
    outCanvas.width = W; outCanvas.height = H;
    const outCtx = outCanvas.getContext('2d');
    if (!outCtx) return null;
    outCtx.strokeStyle = `rgb(${RIM_COLOR[0]},${RIM_COLOR[1]},${RIM_COLOR[2]})`;
    outCtx.lineWidth = RIM_BAND_PX;
    outCtx.lineJoin = 'round';
    outCtx.lineCap = 'round';
    for (const raw of contours) {
      if (raw.length < 6) continue; // need at least 3 points (= 6 numbers)
      const pts = CHAIKIN_ITERATIONS > 0
        ? chaikinSmooth(raw, CHAIKIN_ITERATIONS)
        : raw;
      outCtx.beginPath();
      outCtx.moveTo(pts[0] + 0.5, pts[1] + 0.5);
      for (let i = 2; i < pts.length; i += 2) {
        outCtx.lineTo(pts[i] + 0.5, pts[i + 1] + 0.5);
      }
      outCtx.closePath();
      outCtx.stroke();
    }

    // Optional post-stroke Gaussian blur + re-threshold. Extra-rounds
    // acute centerline joints. Threshold > 128 keeps net thickness on
    // average since the blur falloff is symmetric across the boundary.
    if (SMOOTH_BLUR_PX > 0) {
      const blurCanvas = document.createElement('canvas');
      blurCanvas.width = W; blurCanvas.height = H;
      const blurCtx = blurCanvas.getContext('2d', { willReadFrequently: true });
      if (blurCtx) {
        blurCtx.filter = `blur(${SMOOTH_BLUR_PX}px)`;
        blurCtx.drawImage(outCanvas, 0, 0);
        let blurredData;
        try { blurredData = blurCtx.getImageData(0, 0, W, H); } catch (e) { blurredData = null; }
        if (blurredData) {
          const bd = blurredData.data;
          const rr = RIM_COLOR[0], rg = RIM_COLOR[1], rb = RIM_COLOR[2];
          for (let i = 0; i < N; i++) {
            if (bd[i * 4 + 3] > 128) {
              bd[i * 4]     = rr;
              bd[i * 4 + 1] = rg;
              bd[i * 4 + 2] = rb;
              bd[i * 4 + 3] = 255;
            } else {
              bd[i * 4 + 3] = 0;
            }
          }
          outCtx.putImageData(blurredData, 0, 0);
        }
      }
    }

    let url;
    try { url = await canvasToBlobUrl(outCanvas); } catch (e) { return null; }
    return { url: url, padFraction: PAD / srcW };
  }

  window.MythiumRimColor = { buildRimColors: buildRimColors };
})();
